import { useState } from 'react';
import { api } from '../api';
import { Empty, ErrorBox, Icon, Loading, Metric, PageHead, Panel, Tabs } from '../components/ui';
import { dateLabel, dday, fixed, pending, signedPct } from '../format';
import { useApi } from '../hooks';
import type { Overconfidence, PredictionStatus } from '../types';

type Tab = 'open' | 'due' | 'resolved' | 'all';
const TABS: Tab[] = ['open', 'due', 'resolved', 'all'];

export function StatusBadge({ p }: { p: { status: PredictionStatus; correct: boolean | null; days_left: number } }) {
  if (p.status === 'void') return <span className="badge">무효</span>;
  if (p.status === 'resolved') {
    if (p.correct == null) return <span className="badge">50%</span>;
    return p.correct ? <span className="badge good">맞음</span> : <span className="badge bad">빗나감</span>;
  }
  if (p.days_left <= 0) return <span className="badge warn">결과 입력</span>;
  return <span className="badge">{dday(p.days_left)}</span>;
}

export function verdictText(oc: Overconfidence | null, minN = 10): string {
  if (!oc) return '결과가 나오면 계산합니다';
  switch (oc.verdict) {
    case 'insufficient':
      return pending(oc.n, minN, '건');
    case 'overconfident':
      return '과신: 말한 확신보다 실제 적중이 낮음';
    case 'underconfident':
      return '과소확신: 실제 적중이 말한 확신보다 높음';
    default:
      return '보정 양호: 확신과 적중이 신뢰구간 안에서 맞음';
  }
}

const EMPTY_TEXT: Record<Tab, string> = {
  open: '진행 중인 예측이 없습니다. 결과가 1~4주 안에 나오는 질문 하나를 기록해 보세요.',
  due: '결과를 입력할 예측이 없습니다.',
  resolved: '아직 결과가 나온 예측이 없습니다.',
  all: '예측 기록이 없습니다.',
};

export function Decisions({ initialTab = 'open' }: { initialTab?: string }) {
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab as Tab) ? (initialTab as Tab) : 'open');
  const list = useApi(() => api.predictions(tab), [tab]);
  const stats = useApi(api.decisionStats);
  const s = stats.data;
  return (
    <>
      <PageHead
        title="판단 연습"
        sub="확률로 예측하고 결과로 채점받기. 브라이어 점수는 낮을수록 좋고, 0.25는 '늘 50%'라고 말한 수준입니다."
        actions={<a className="btn btn-primary" href="#/decisions/new"><Icon name="plus" size={16} />새 예측</a>}
      />
      {s && (
        <div className="panel metrics-row">
          <Metric label="진행 중" value={s.counts.open} sub={s.counts.due ? `결과 입력 필요 ${s.counts.due}건` : '밀린 결과 없음'} />
          <Metric label="결과 나온 예측" value={s.counts.resolved} sub={s.counts.void ? `무효 ${s.counts.void}건` : '무효 0건'} />
          <Metric
            label="브라이어 (전체)"
            value={fixed(s.brier.all.value)}
            sub={`n=${s.brier.all.n}`}
            insufficient={s.brier.all.n < s.min_n ? pending(s.brier.all.n, s.min_n, '건') : undefined}
          />
          <Metric label="과신 지수" value={s.overconfidence ? signedPct(s.overconfidence.gap) : '—'} sub={verdictText(s.overconfidence, s.min_n)} />
        </div>
      )}
      <Tabs
        label="예측 목록"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'open', label: '진행 중' },
          { key: 'due', label: `결과 입력${s?.counts.due ? ` (${s.counts.due})` : ''}` },
          { key: 'resolved', label: '완료' },
          { key: 'all', label: '전체' },
        ]}
      />
      {list.error ? (
        <ErrorBox message={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading />
      ) : list.data.length === 0 ? (
        <Panel><Empty>{EMPTY_TEXT[tab]}</Empty></Panel>
      ) : (
        <ul className="item-list">
          {list.data.map((p) => (
            <li key={p.id}>
              <a className="panel item-row" href={`#/decisions/${p.id}`}>
                <span className="prob big">{Math.round(p.prob_current * 100)}%</span>
                <div className="grow">
                  <b className="clamp-2">{p.question}</b>
                  <p className="muted small">
                    {p.kind === 'decision' && '결정 · '}
                    {p.domain && `${p.domain} · `}마감 {dateLabel(p.resolve_by)}
                    {p.prob_initial !== p.prob_current && ` · 처음 ${Math.round(p.prob_initial * 100)}%`}
                  </p>
                </div>
                <div className="item-side">
                  <StatusBadge p={p} />
                  {p.brier != null && <span className="muted small">브라이어 {p.brier.toFixed(2)}</span>}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
